import { Button, Input, message, Modal, Pagination, Select, Tabs } from "antd";
import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import ClockInModel from "../../component/clockInModel";
import LoadingBox from "../../component/loadingBox";
import NoData from "../../component/nodata";
import OuterPageHeader from "../../component/OuterHeader/OuterPageHeader";
import Header from "../../component/retailHeader/header";
import API from "../../config/api";
import { GET, POST, POST2 } from "../../utils/apiCalls";
import ProductForm from "./components/form-product";
import ServiceForm from "./components/form-service";
import ProductItemList from "./components/productItem";
import "./styles.scss";

function RetailProduct() {
  // Note: If you see "useForm is not connected to any Form element" warning,
  // it might be coming from ProductForm or ServiceForm components when they're
  // conditionally rendered. This is usually harmless but can be fixed by ensuring
  // the form is properly connected to a Form component.

  const { t } = useTranslation();
  const { user } = useSelector((state: any) => state.User);
  const { counter } = useSelector((state: any) => state.retailExpress);

  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<any>({});

  const [categories, setCategories] = useState<any>([]);
  const [data, setData] = useState<any>([]);
  const [product, setProduct] = useState<string | undefined>();
  const [category, setCategory] = useState<any>(null);
  const [loding, setLoading] = useState<any>(true);
  const [productCModal, setProductCModal] = useState(false);
  const [type, setType] = useState("both");
  const [selectBank, setSlectedBank] = useState<any>([]);
  const [counterModal, setCounterModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Branch related state
  const [branches, setBranches] = useState<any>([]);
  const [selectedBranch, setSelectedBranch] = useState<any>(
    user?.staff?.companyid || null
  );
  const [productsByBranch, setProductsByBranch] = useState<any>({});

  useEffect(() => {
    getBranches();
    getCategories();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      getProductsForBranch(selectedBranch.id);
    }
  }, [selectedBranch, category, product, page, type]);

  const getCategories = async (branchId?: number) => {
    try {
      const companyId = branchId || user?.staff?.companyid;
      const url = `ProductCategory/user/` + user?.id + "/" + companyId;
      const response: any = await GET(url, {});
      if (response?.data?.length) {
        // Create an array of category objects
        const categoryObjects = response?.data?.map((item: any) => ({
          key: item?.id,
          label: item?.category?.toUpperCase(),
        }));

        // Sort categories alphabetically by label
        categoryObjects.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );

        // Add sorted categories to data array
        data.push(...categoryObjects);
      }
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const getBranches = async () => {
    try {
      let url = API.BRANCHES_LIST;
      if (!user?.staff?.staffAccess?.includes("administrator")) {
        url = API.BRANCHES_LIST + `branchId=${user?.staff?.companyid}`;
      }
      const response: any = await GET(url, {});
      if (response?.data && Array.isArray(response.data)) {
        const formattedBranches = response.data.map((item: any) => ({
          id: item.CompanyMaster.id,
          bname: item.CompanyMaster.bname,
          fulladdress: item.CompanyMaster.fulladdress,
        }));
        setBranches(formattedBranches);
        if (selectedBranch == null) {
          setSelectedBranch(formattedBranches[0]?.id);
          getProductsForBranch(formattedBranches[0]?.id);
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const handleExcelExport = async () => {
    try {
      setExportLoading(true);
      const branchId = selectedBranch?.id || "all";
      const companyId = branchId !== "all" ? branchId : user?.staff?.companyid;

      let body = {
        id: user?.id,
        companyid: companyId,
        type: type,
        category: category,
        name: product || 0,
        barcode: null,
        location: type === "Service" ? null : counter?.counter?.location,
        branchId: branchId !== "all" ? branchId : null,
      };

      const url = `retail/productlist?order=DESC&page=1&take=1000`;
      const response: any = await POST(url, body);

      if (
        response?.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        // Convert the data to Excel and download
        const excelBuffer = convertToExcel(response.data);
        const filename = `products_${companyId}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        downloadExcel(excelBuffer, filename);
      } else {
        throw new Error("No products found to export");
      }
    } catch (error: any) {
      console.error("Error exporting Excel:", error);
      alert(
        `Failed to export Excel file: ${
          error.message || "Unknown error"
        }. Please try again.`
      );
    } finally {
      setExportLoading(false);
    }
  };

  // Helper function to convert data to Excel
  const convertToExcel = (data: any[]) => {
    if (!data.length) return null;

    // Transform data for Excel
    const excelData = data.map((item, index) => ({
      si_number: index + 1,
      id: item.id,
      name: item.idescription || item.name || "",
      product_category: item.productCategory?.category || "",
      current_price: item.sp_price || item.rate || 0,
      new_price: "",
    }));

    // Create Excel workbook
    const workbook = XLSX.utils.book_new();

    const headers = [
      "SI Number",
      "ID",
      "Name",
      "Product Category",
      "Current Price",
      "New Price",
    ];

    // Convert data to worksheet format
    const worksheetData = [
      headers,
      ...excelData.map((item) => [
        item.si_number,
        item.id,
        item.name,
        item.product_category,
        item.current_price,
        item.new_price,
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    worksheet["!cols"] = [
      { width: 10 }, // SI Number
      { width: 10 }, // ID
      { width: 30 }, // Name
      { width: 20 }, // Product Category
      { width: 15 }, // Current Price
      { width: 15 }, // New Price
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");

    // Generate Excel buffer
    const excelBuffer = XLSX.write(workbook, {
      type: "buffer",
      bookType: "xlsx",
    });

    return excelBuffer;
  };

  // Helper function to download Excel
  const downloadExcel = (excelBuffer: any, filename: string) => {
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleExcelUpload = async () => {
    if (!selectedFile) {
      message.error("Please select a file first");
      return;
    }

    try {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Add company ID - use selectedBranch.id or user's company ID
      const companyId = selectedBranch?.id || user?.staff?.companyid;
      formData.append("companyId", companyId.toString());

      const url = API.PRODUCT_PRICE_UPLOAD;
      const response: any = await POST2(url, formData);

      if (response?.status) {
        setUploadModal(false);
        setSelectedFile(null);
        // Refresh products after successful upload
        if (selectedBranch) {
          getProductsForBranch(selectedBranch.id);
        }
      } else {
        // Handle error response
        console.log(response?.message || "Upload failed. Please try again.");
      }
    } catch (error: any) {
      console.log("Error uploading Excel:", error);
    } finally {
      setUploadLoading(false);
    }
  };

  let radiButtunType = [
    { name: "both", label: (t as any)("header.text") },
    { name: "Service", label: (t as any)("header.text_1") },
  ];

  const handleBranchChange = (value: string) => {
    // Clear existing data when changing branches
    setData([]);
    setProductsByBranch({});
    setPage(1);
    setCategory(0); // Reset to ALL PRODUCTS when changing branches

    if (value === "all") {
      setSelectedBranch(null);
      getCategories(); // Get all categories
    } else {
      const branch = branches.find((b: any) => b.id === value);
      setSelectedBranch(branch);
      getCategories(Number(value)); // Get branch-specific categories
    }
  };

  // Separate function to get products for a specific branch
  const getProductsForBranch = async (branchId: string) => {
    try {
      setLoading(true);
      let body = {
        id: user?.id,
        companyid: branchId || selectedBranch?.id || user?.staff?.companyid,
        type: type,
        category: category,
        name: product || 0,
        barcode: null,
        location: type === "Service" ? null : counter?.counter?.location,
        branchId: branchId,
      };

      const url = `retail/productlist?order=DESC&page=${page}&take=8`;
      const response: any = await POST(url, body);

      if (response?.data?.length) {
        response.data.forEach((item: any) => {
          item["selection"] = false;
        });
        setData(response.data);
        setMeta(response?.meta);
      } else {
        setData([]);
        setMeta({});
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products for branch:", error);
      setData([]);
      setLoading(false);
    }
  };

  return (
    <>
      <Header
        product={(val: any) => setProduct(val)}
        type={type}
        typeList={radiButtunType}
        setType={(val: any) => {
          setPage(1);
          setType(val);
        }}
      />
      <div className="product">
        <br />
        <Container>
          <OuterPageHeader
            title={(t as any)("product.text")}
            children={
              <div className="excel-actions">
                <Button
                  type="primary"
                  onClick={() => {
                    if (counter?.id) {
                      setProductCModal(true);
                    } else {
                      setCounterModal(true);
                    }
                  }}
                >
                  {(t as any)("product.text_1")}{" "}
                  {type == "both"
                    ? (t as any)("product.text_1.1")
                    : (t as any)("product.text_1.2")}
                </Button>
                <Button
                  type="default"
                  className="export-btn"
                  loading={exportLoading}
                  onClick={handleExcelExport}
                >
                  Export Excel
                </Button>
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={() => setUploadModal(true)}
                >
                  Upload Excel
                </Button>
              </div>
            }
          />
          <br />
          <Row>
            <Col sm={12} xs={12} style={{ margin: 0, padding: 0 }}>
              <div className="filters-row mb-3">
                <div className="search-filter-section">
                  <Input.Search
                    placeholder="Search products..."
                    allowClear
                    enterButton="Search"
                    size="large"
                    onSearch={(value) => {
                      setProduct(value);
                      setPage(1);
                    }}
                    style={{ maxWidth: 500 }}
                  />
                </div>
                {user?.staff?.staffAccess?.includes("administrator") ? (
                  <div className="branch-filter-section">
                    <Select
                      placeholder="Select Branch"
                      style={{ width: 300 }}
                      value={selectedBranch?.id || branches[0]?.id}
                      onChange={handleBranchChange}
                      options={[
                        ...branches.map((branch: any) => ({
                          value: branch.id,
                          label: `${branch.bname} - ${branch.fulladdress}`,
                        })),
                      ]}
                    />
                  </div>
                ) : null}
              </div>

              <Tabs
                size="small"
                defaultActiveKey={category?.toString() || "0"}
                items={categories}
                onChange={(val: any) => {
                  setCategory(val);
                  setPage(1);
                }}
                tabBarStyle={{
                  backgroundColor: "white",
                  margin: 0,
                  padding: "0px 10px",
                }}
              />
              <br />
              <div className="product-box1">
                <Row className="mt-3">
                  {loding ? (
                    <LoadingBox />
                  ) : selectedBranch ? (
                    // Show products for selected branch
                    <>
                      {data && data?.length ? (
                        data.map((item: any, index: number) => (
                          <Col
                            sm={6}
                            xs={12}
                            lg={3}
                            key={index}
                            style={{ marginBottom: 20 }}
                          >
                            <ProductItemList
                              bankList={selectBank}
                              item={item}
                              currencycode={
                                user?.companyInfo?.countryInfo?.symbol
                              }
                              getDatas={() =>
                                getProductsForBranch(selectedBranch.id)
                              }
                              setCounterModal={() => setCounterModal(true)}
                              counter={counter?.id ? true : false}
                              type={type}
                            />
                          </Col>
                        ))
                      ) : (
                        <NoData text={(t as any)("product.text_4")} />
                      )}
                    </>
                  ) : // Show products grouped by branch
                  Object.keys(productsByBranch).length > 0 ? (
                    <>
                      <div className="all-branches-summary mb-3">
                        <h5>All Branches Summary</h5>
                        <p>
                          Total Products:{" "}
                          {Object.values(productsByBranch).reduce(
                            (total: number, branchData: any) =>
                              total + (branchData.products?.length || 0),
                            0
                          )}
                        </p>
                      </div>
                      {Object.values(productsByBranch).map(
                        (branchData: any, branchIndex: number) => (
                          <Col
                            sm={12}
                            key={branchIndex}
                            style={{ marginBottom: 30 }}
                          >
                            <div className="branch-section">
                              <div className="branch-header">
                                <h4>{branchData.branch.bname}</h4>
                                <p className="branch-address">
                                  {branchData.branch.fulladdress}
                                </p>
                              </div>
                              <Row>
                                {branchData.products &&
                                branchData.products.length > 0 ? (
                                  branchData.products.map(
                                    (item: any, index: number) => (
                                      <Col
                                        sm={6}
                                        xs={12}
                                        lg={3}
                                        key={index}
                                        style={{ marginBottom: 20 }}
                                      >
                                        <ProductItemList
                                          bankList={selectBank}
                                          item={item}
                                          currencycode={
                                            user?.companyInfo?.countryInfo
                                              ?.symbol
                                          }
                                          getDatas={() =>
                                            selectedBranch
                                              ? getProductsForBranch(
                                                  selectedBranch.id
                                                )
                                              : getProductsForBranch(
                                                  user?.staff?.companyid
                                                )
                                          }
                                          setCounterModal={() =>
                                            setCounterModal(true)
                                          }
                                          counter={counter?.id ? true : false}
                                          type={type}
                                        />
                                      </Col>
                                    )
                                  )
                                ) : (
                                  <Col sm={12}>
                                    <NoData
                                      text={`No products found for ${branchData.branch.bname}`}
                                    />
                                  </Col>
                                )}
                              </Row>
                            </div>
                          </Col>
                        )
                      )}
                    </>
                  ) : (
                    <NoData text={(t as any)("product.text_4")} />
                  )}
                </Row>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                }}
              >
                {selectedBranch && (
                  <Pagination
                    total={meta?.itemCount}
                    showSizeChanger={false}
                    showTotal={(total) =>
                      `${(t as any)("product.text_2")} ${meta?.itemCount} ${(
                        t as any
                      )("product.text_3")}`
                    }
                    onChange={(page) => setPage(page)}
                  />
                )}
              </div>
              <br />
              <br />
              <br />
            </Col>
          </Row>
        </Container>
        {productCModal && type == "Service" ? (
          <ServiceForm
            productCModal={productCModal}
            close={() => setProductCModal(false)}
            getDatas={() =>
              selectedBranch
                ? getProductsForBranch(selectedBranch.id)
                : getProductsForBranch(user?.staff?.companyid)
            }
            url={"ProductMaster/add"}
          />
        ) : (
          <ProductForm
            productCModal={productCModal}
            close={() => setProductCModal(false)}
            getDatas={() =>
              selectedBranch
                ? getProductsForBranch(selectedBranch.id)
                : getProductsForBranch(user?.staff?.companyid)
            }
            url={API.PURCHASE_PRODUCT}
          />
        )}
        {counterModal ? (
          <ClockInModel
            open={counterModal}
            onCancel={() => setCounterModal(false)}
            text={(t as any)("product.text_18")}
          />
        ) : null}

        {/* Excel Upload Modal */}
        <Modal
          title="Upload Excel File"
          open={uploadModal}
          className="upload-modal"
          onCancel={() => {
            setUploadModal(false);
            setSelectedFile(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setUploadModal(false);
                setSelectedFile(null);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="upload"
              type="primary"
              loading={uploadLoading}
              onClick={handleExcelUpload}
              disabled={!selectedFile}
            >
              Upload
            </Button>,
          ]}
        >
          <div>
            <p style={{ marginBottom: "15px", color: "#666" }}>
              {selectedBranch
                ? `Upload Excel file for branch: ${selectedBranch?.bname}`
                : "Upload Excel file for all branches"}
            </p>
            <div className="file-input-container">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileSelect(file);
                  }
                }}
              />
            </div>
            {selectedFile && (
              <div className="selected-file">
                Selected file: {selectedFile.name}
              </div>
            )}
          </div>
        </Modal>
      </div>
    </>
  );
}

export default RetailProduct;
